import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Building2,
  Mail,
  Phone,
  Lock,
  User,
  FileText,
  MapPin,
  Briefcase,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Eye,
  EyeOff,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { saasApi } from "../api/saasApi";
import { validateRegistrationForm, mapApiErrors, PASSWORD_HINT } from "../utils/validation";
import { persistAdminSession } from "../utils/session";
import brandIcon from "../../../Img/100BILLION_ICON.png";
import brandName from "../../../Img/100BILLION_TECH_name.png";
import { TERMS_VERSION, PRIVACY_VERSION } from "../../../Components/Legal/legalContent";
import "../styles/saas.css";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token");

  const [step, setStep] = useState(tokenFromUrl ? "verify" : "form");
  const [form, setForm] = useState({
    plan_slug: searchParams.get("plan") || "starter",
    company_name: "",
    email: "",
    mobile: "",
    password: "",
    owner_name: "",
    gst_number: "",
    address: "",
    industry: "",
  });
  const [verify, setVerify] = useState({ otp: "" });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!tokenFromUrl) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await saasApi.verifyEmail({ token: tokenFromUrl });
        if (cancelled) return;
        persistAdminSession(res.data);
        window.location.href = "/dashboard";
      } catch (err) {
        if (cancelled) return;
        setMessage(
          err.data?.message ||
            "This verification link expired. Enter the 6-digit code from your email, or resend a new code."
        );
        setMessageType("error");
        setStep("verify");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tokenFromUrl]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submitRegister = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!acceptedTerms) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy");
      setErrors((prev) => ({
        ...prev,
        acceptedTerms: "You must accept the Terms & Privacy Policy",
      }));
      return;
    }

    const v = validateRegistrationForm(form);
    setErrors(v);
    if (Object.keys(v).length) return;

    setLoading(true);
    try {
      const res = await saasApi.register({
        ...form,
        plan_slug: form.plan_slug || searchParams.get("plan") || "starter",
        accepted_terms: true,
        terms_version: TERMS_VERSION,
        privacy_version: PRIVACY_VERSION,
      });
      setMessage(res.message || "Verification code sent. Check your inbox and spam folder.");
      setMessageType("success");
      toast.success(res.message || "Verification code sent");
      setStep("verify");
      setErrors({});
    } catch (err) {
      setMessageType("error");
      const apiErrors = mapApiErrors(err.data?.errors);
      if (Object.keys(apiErrors).length) {
        setErrors(apiErrors);
        setMessage("Please fix the highlighted fields.");
      } else {
        const msg = err.data?.message || err.message || "Registration failed";
        setMessage(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setMessage("");
    const otpDigits = verify.otp.replace(/\D/g, "");
    if (!tokenFromUrl && otpDigits.length !== 6) {
      setMessageType("error");
      setMessage("Enter the 6-digit code from your email.");
      return;
    }

    setLoading(true);
    try {
      const res = await saasApi.verifyEmail({
        token: tokenFromUrl || undefined,
        otp: otpDigits || undefined,
      });
      persistAdminSession(res.data);
      window.location.href = "/dashboard";
    } catch (err) {
      setMessageType("error");
      const msg = err.data?.message || err.message || "Verification failed";
      setMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (!form.email?.trim()) {
      setMessageType("error");
      setMessage("Go back and complete the signup form first.");
      setStep("form");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      const res = form.password
        ? await saasApi.resendVerification(form)
        : await saasApi.resendVerificationEmail(form.email);
      setMessage(res.message || "A new verification code has been sent.");
      setMessageType("success");
      toast.success(res.message || "A new verification code has been sent.");
    } catch (err) {
      setMessageType("error");
      const msg = err.data?.message || err.message || "Could not resend code";
      setMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "login-auth-input w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-11 pr-4 text-sm text-white placeholder-white/30 outline-none transition focus:border-cyan-400/60 focus:bg-white/5 focus:ring-2 focus:ring-cyan-500/30";

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="relative min-h-screen w-full overflow-hidden bg-[#0a0f2e] font-sans text-white">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute -left-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-cyan-500/35 blur-[120px]"
            animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
            transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute -right-40 top-10 h-[30rem] w-[30rem] rounded-full bg-fuchsia-500/30 blur-[120px]"
            animate={{ x: [0, -50, 0], y: [0, 60, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-[-12rem] left-1/3 h-[32rem] w-[32rem] rounded-full bg-lime-400/20 blur-[140px]"
            animate={{ x: [0, 40, 0], y: [0, -40, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          />
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />
        </div>

        <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
          <div className="grid w-full overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] shadow-2xl backdrop-blur-xl lg:grid-cols-2">
            {/* Left brand panel */}
            <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-cyan-600/30 via-indigo-800/40 to-fuchsia-600/30 p-10 lg:flex">
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full border border-white/10" />
              <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full border border-white/10" />

              <div className="relative flex items-center gap-3">
                <img src={brandIcon} alt="100 Billion" className="h-11 w-11 object-contain drop-shadow-lg" />
                <img src={brandName} alt="100 Billion Tech" className="h-7 object-contain brightness-0 invert" />
              </div>

              <div className="relative">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white/80"
                >
                  <Sparkles size={14} className="text-lime-300" />
                  Start free today
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="text-4xl font-bold leading-tight tracking-tight"
                >
                  Create your<br />workspace and<br />
                  <span className="bg-gradient-to-r from-cyan-300 via-lime-300 to-fuchsia-300 bg-clip-text text-transparent">
                    grow without limits.
                  </span>
                </motion.h1>
                <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/70">
                  Join 100+ brands and 1000+ sellers already running on Beentara.
                </p>
              </div>

              <div className="relative grid grid-cols-3 gap-4">
                {[
                  { k: "14-day", v: "Free trial" },
                  { k: "No card", v: "To start" },
                  { k: "Cancel", v: "Anytime" },
                ].map((s) => (
                  <div key={s.v} className="rounded-2xl border border-white/10 bg-white/5 px-3 py-3 text-center">
                    <div className="text-lg font-bold text-white">{s.k}</div>
                    <div className="text-[11px] uppercase tracking-wider text-white/55">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right auth panel */}
            <div className="flex max-h-[90vh] flex-col overflow-hidden">
              <div className="auth-signup-scroll flex-1 overflow-y-auto overscroll-contain p-7 sm:p-10">
              <div className="mb-6 flex items-center gap-3 lg:hidden">
                <img src={brandIcon} alt="100 Billion" className="h-10 w-10 object-contain" />
                <img src={brandName} alt="100 Billion Tech" className="h-6 object-contain brightness-0 invert" />
              </div>

              <h2 className="text-2xl font-bold tracking-tight">
                {step === "form" ? "Create your account" : "Verify your email"}
              </h2>
              <p className="mt-1 text-sm text-white/60">
                {step === "form"
                  ? "Fill in your details to start your free trial."
                  : `We sent a 6-digit code to ${form.email || "your email"}.`}
              </p>

              {message && (
                <p
                  className={`mt-4 rounded-xl border px-3 py-2 text-sm ${
                    messageType === "error"
                      ? "border-red-400/30 bg-red-500/10 text-red-200"
                      : messageType === "success"
                        ? "border-lime-400/30 bg-lime-500/10 text-lime-200"
                        : "border-white/10 bg-white/5 text-white/70"
                  }`}
                >
                  {message}
                </p>
              )}

              <div className="mt-6">
                <AnimatePresence mode="wait">
                  {step === "form" ? (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16 }}
                      transition={{ duration: 0.25 }}
                      onSubmit={submitRegister}
                      className="space-y-3.5"
                    >
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-white/70">Company name *</label>
                        <div className="group relative">
                          <Building2 size={18} className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-cyan-400/70" />
                          <input
                            name="company_name"
                            value={form.company_name}
                            onChange={onChange}
                            placeholder="Your company"
                            className={fieldClass}
                          />
                        </div>
                        {errors.company_name && <p className="mt-1 text-xs text-red-300">{errors.company_name}</p>}
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-white/70">Email *</label>
                        <div className="group relative">
                          <Mail size={18} className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-cyan-400/70" />
                          <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={onChange}
                            placeholder="you@company.com"
                            autoComplete="email"
                            className={fieldClass}
                          />
                        </div>
                        {errors.email && <p className="mt-1 text-xs text-red-300">{errors.email}</p>}
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-white/70">Mobile *</label>
                        <div className="group relative">
                          <Phone size={18} className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-cyan-400/70" />
                          <input
                            name="mobile"
                            value={form.mobile}
                            onChange={onChange}
                            placeholder="10-digit number"
                            className={fieldClass}
                          />
                        </div>
                        {errors.mobile && <p className="mt-1 text-xs text-red-300">{errors.mobile}</p>}
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-white/70">Password *</label>
                        <div className="group relative">
                          <Lock size={18} className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-cyan-400/70" />
                          <input
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={form.password}
                            onChange={onChange}
                            autoComplete="new-password"
                            placeholder="Create a strong password"
                            className={`${fieldClass} pr-11`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((p) => !p)}
                            className="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-cyan-400/70 hover:text-cyan-300"
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        <p className="mt-1 text-[11px] text-white/40">{PASSWORD_HINT}</p>
                        {errors.password && <p className="mt-1 text-xs text-red-300">{errors.password}</p>}
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-white/70">Owner name</label>
                        <div className="group relative">
                          <User size={18} className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-cyan-400/70" />
                          <input
                            name="owner_name"
                            value={form.owner_name}
                            onChange={onChange}
                            placeholder="Full name"
                            className={fieldClass}
                          />
                        </div>
                      </div>

                      <div className="grid gap-3.5 sm:grid-cols-2">
                        <div>
                          <label className="mb-1.5 block text-xs font-medium text-white/70">GST number</label>
                          <div className="group relative">
                            <FileText size={18} className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-cyan-400/70" />
                            <input
                              name="gst_number"
                              value={form.gst_number}
                              onChange={onChange}
                              placeholder="Optional"
                              className={fieldClass}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-medium text-white/70">Industry</label>
                          <div className="group relative">
                            <Briefcase size={18} className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-cyan-400/70" />
                            <input
                              name="industry"
                              value={form.industry}
                              onChange={onChange}
                              placeholder="Optional"
                              className={fieldClass}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-white/70">Address</label>
                        <div className="group relative">
                          <MapPin size={18} className="pointer-events-none absolute left-3.5 top-3.5 z-10 text-cyan-400/70" />
                          <textarea
                            name="address"
                            value={form.address}
                            onChange={onChange}
                            placeholder="Business address"
                            rows={2}
                            className="login-auth-input w-full resize-none rounded-xl border border-white/10 bg-white/5 py-3 pl-11 pr-4 text-sm text-white placeholder-white/30 outline-none transition focus:border-cyan-400/60 focus:bg-white/5 focus:ring-2 focus:ring-cyan-500/30"
                          />
                        </div>
                      </div>

                      <label className="flex cursor-pointer items-start gap-2.5 select-none pt-1">
                        <input
                          type="checkbox"
                          checked={acceptedTerms}
                          onChange={(e) => {
                            setAcceptedTerms(e.target.checked);
                            if (e.target.checked) {
                              setErrors((prev) => {
                                const next = { ...prev };
                                delete next.acceptedTerms;
                                return next;
                              });
                            }
                          }}
                          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded accent-cyan-500"
                        />
                        <span className="text-xs leading-relaxed text-white/65">
                          I agree to the{" "}
                          <Link
                            to="/terms-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan-300 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Terms &amp; Conditions
                          </Link>{" "}
                          and{" "}
                          <Link
                            to="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan-300 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Privacy Policy
                          </Link>
                        </span>
                      </label>
                      {errors.acceptedTerms && (
                        <p className="text-xs text-red-300">{errors.acceptedTerms}</p>
                      )}

                      <button
                        type="submit"
                        disabled={loading || !acceptedTerms}
                        className="group relative mt-1 flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 via-violet-500 to-fuchsia-500 py-3 text-sm font-semibold text-white shadow-lg shadow-fuchsia-900/40 transition hover:shadow-fuchsia-700/50 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {loading ? (
                          <>
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                            Sending…
                          </>
                        ) : (
                          <>
                            Continue
                            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                          </>
                        )}
                      </button>
                    </motion.form>
                  ) : (
                    <motion.form
                      key="verify"
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 16 }}
                      transition={{ duration: 0.25 }}
                      onSubmit={submitVerify}
                      className="space-y-4"
                    >
                      <div className="mb-2 flex flex-col items-center text-center">
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-fuchsia-500 shadow-lg">
                          <ShieldCheck size={22} className="text-white" />
                        </div>
                        <p className="text-xs text-white/55">
                          Check spam/promotions if you do not see the email.
                        </p>
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-white/70">Verification code</label>
                        <input
                          value={verify.otp}
                          onChange={(e) =>
                            setVerify({ otp: e.target.value.replace(/\D/g, "").slice(0, 6) })
                          }
                          placeholder="123456"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-lg font-semibold tracking-[0.4em] text-white outline-none transition focus:border-cyan-400/60 focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/30"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="group flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 via-violet-500 to-fuchsia-500 py-3 text-sm font-semibold text-white shadow-lg transition disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {loading ? "Verifying…" : "Create workspace"}
                        {!loading && <ArrowRight size={18} />}
                      </button>

                      <button
                        type="button"
                        onClick={resendCode}
                        disabled={loading}
                        className="w-full rounded-xl border border-white/15 bg-white/5 py-2.5 text-sm font-semibold text-white/80 transition hover:bg-white/10 disabled:opacity-70"
                      >
                        Resend code
                      </button>

                      <button
                        type="button"
                        onClick={() => setStep("form")}
                        className="w-full text-center text-xs text-white/50 hover:text-cyan-300"
                      >
                        ← Edit registration details
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>

              <p className="mt-6 text-center text-sm text-white/55">
                Already have an account?{" "}
                <Link to="/login" className="font-semibold text-cyan-300 hover:underline">
                  Log in
                </Link>
              </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
